export type UnitDimension = 'mass' | 'volume' | 'count';

/** Fixed FR metric volume → mL (not US customary). */
export const VOLUME_TO_ML: Record<string, number> = {
  milliliter: 1,
  centiliter: 10,
  deciliter: 100,
  liter: 1000,
  teaspoon: 5,
  tablespoon: 15,
  cup: 250,
};

const MASS = new Set(['gram', 'kilogram']);
const COUNT = new Set(['piece']);
const VOLUME = new Set(Object.keys(VOLUME_TO_ML));

export function getUnitDimension(unit: string | null | undefined): UnitDimension | null {
  const u = String(unit || '').trim().toLowerCase();
  if (!u) return null;
  if (MASS.has(u)) return 'mass';
  if (COUNT.has(u)) return 'count';
  if (VOLUME.has(u)) return 'volume';
  return null;
}

export function volumeToMl(amount: number, unit: string): number {
  const factor = VOLUME_TO_ML[String(unit || '').trim().toLowerCase()];
  if (factor == null || !Number.isFinite(amount) || amount <= 0) {
    throw new Error(`Invalid volume: ${amount} ${unit}`);
  }
  return amount * factor;
}

export function normalizeDensity(
  weightG: number,
  volumeAmount: number,
  volumeUnit: string
): number {
  if (!Number.isFinite(weightG) || weightG <= 0) {
    throw new Error('weightG must be a positive number');
  }
  return weightG / volumeToMl(volumeAmount, volumeUnit);
}

/**
 * Every ingredient eventually needs a conversion field:
 * - mass / piece → unitWeight_g (recipes may use "4 amandes" even if preferred is gram)
 * - volume → density_g_per_mL
 */
export function needsUnitConversion(ingredient: {
  preferredUnit?: string | null;
}): boolean {
  const dim = getUnitDimension(ingredient.preferredUnit);
  return dim === 'mass' || dim === 'count' || dim === 'volume';
}

/** True when the relevant conversion field is filled. */
export function hasConversionComplete(ingredient: {
  preferredUnit?: string | null;
  unitWeight_g?: number | null;
  density_g_per_mL?: number | null;
}): boolean {
  const dim = getUnitDimension(ingredient.preferredUnit);
  if (dim === 'mass' || dim === 'count') {
    return (
      typeof ingredient.unitWeight_g === 'number' &&
      Number.isFinite(ingredient.unitWeight_g) &&
      ingredient.unitWeight_g > 0
    );
  }
  if (dim === 'volume') {
    return (
      typeof ingredient.density_g_per_mL === 'number' &&
      Number.isFinite(ingredient.density_g_per_mL) &&
      ingredient.density_g_per_mL > 0
    );
  }
  return false;
}

export function hasCiqualNutrition(ingredient: {
  nutrition?: { source?: string | null } | null;
}): boolean {
  return ingredient.nutrition?.source === 'ciqual';
}
